"use client";

import { AdvancedMarker, Map } from "@vis.gl/react-google-maps";
import { Badge } from "@yardzen/ui/components/badge";
import { Button } from "@yardzen/ui/components/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@yardzen/ui/components/dialog";
import { H2 } from "@yardzen/ui/components/h2";
import { VisuallyHidden } from "@yardzen/ui/components/visually-hidden";
import Image from "next/image";
import { useEffect, useState } from "react";
import { getWeatherAction } from "../../actions";

export default function AddressConfirmation({
  open,
  onOpenChange,
  selectedPlace,
  onGoBack,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedPlace: google.maps.places.PlaceResult | null;
  onGoBack: () => void;
}) {
  const [weather, setWeather] = useState<{
    temp?: number;
    condition?: string;
    icon?: string;
  }>({
    temp: undefined,
    condition: undefined,
    icon: undefined,
  });
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  useEffect(() => {
    const fetchWeather = async () => {
      if (!selectedPlace?.geometry?.location) {
        console.warn("No location found");
        return;
      }
      try {
        const lat = selectedPlace.geometry.location.lat();
        const lng = selectedPlace.geometry.location.lng();

        const weatherData = await getWeatherAction(lat, lng);
        if (!weatherData) {
          console.warn("Weather data is undefined");
          return;
        }
        setWeather({
          temp: weatherData.temp,
          condition: weatherData.condition,
          icon: weatherData.icon,
        });
      } catch (error) {
        console.error("Failed to set weather:", error);
      }
    };

    fetchWeather();
  }, [selectedPlace?.geometry?.location]);

  const city =
    selectedPlace?.address_components?.find((component) =>
      component.types.includes("locality"),
    )?.long_name ??
    selectedPlace?.address_components?.find((component) =>
      component.types.includes("neighborhood"),
    )?.long_name;
  const streetNumber = selectedPlace?.address_components?.find((component) =>
    component.types.includes("street_number"),
  )?.long_name;
  const street = selectedPlace?.address_components?.find((component) =>
    component.types.includes("route"),
  )?.long_name;

  const center = {
    lat: selectedPlace?.geometry?.location?.lat() || 0,
    lng: selectedPlace?.geometry?.location?.lng() || 0,
  };

  const streetAddress = `${streetNumber} ${street}, ${city}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-brand-moss-greens-20 max-h-[90vh] max-w-2xl overflow-y-auto p-0">
        <DialogHeader>
          <VisuallyHidden>
            <DialogTitle>Confirm Address</DialogTitle>
            <DialogDescription>We love designing in {city}!</DialogDescription>
          </VisuallyHidden>
        </DialogHeader>
        <div className="flex h-full flex-col">
          <div className="flex-1 overflow-y-auto">
            <div className="flex flex-col gap-4 p-6">
              <div className="text-center">
                <H2 className="font-display mb-2 text-4xl">
                  We love designing in {city}!
                </H2>
                <div className="mt-4 flex flex-row justify-center gap-2">
                  <Badge hover={false} variant="secondary">
                    {streetAddress}
                  </Badge>
                  {weather.temp && weather.icon && (
                    <Badge hover={false} variant="secondary">
                      <Image
                        src={`https://openweathermap.org/img/wn/${weather.icon}@2x.png`}
                        alt={weather.condition || "Weather icon"}
                        width={24}
                        height={24}
                        className="h-6 w-6"
                      />
                      {weather.condition}, {weather.temp < 0 ? "-" : ""}
                      {Math.abs(Math.round(weather.temp))}°F
                    </Badge>
                  )}
                </div>
              </div>

              <div
                className="relative w-full overflow-hidden rounded-2xl"
                style={{ height: "400px" }}
              >
                {!isMapLoaded && (
                  <div className="absolute inset-0 animate-pulse bg-gray-200" />
                )}

                {selectedPlace?.geometry?.location && (
                  <div
                    className="absolute inset-0 transition-opacity duration-300"
                    style={{ opacity: isMapLoaded ? 1 : 0 }}
                  >
                    <Map
                      defaultCenter={center}
                      defaultZoom={19}
                      gestureHandling="none"
                      disableDefaultUI={true}
                      mapTypeId="satellite"
                      mapId="address-confirmation-map"
                      onTilesLoaded={() => setIsMapLoaded(true)}
                    >
                      <AdvancedMarker position={center} title={streetAddress} />
                    </Map>
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="flex flex-col gap-6 p-6">
            <DialogClose asChild>
              <Button className="w-full" variant="outline" onClick={onGoBack}>
                That&apos;s not my address
              </Button>
            </DialogClose>
            <Button
              className="w-full"
              form="design-consultation-quiz-form"
              type="submit"
            >
              Continue
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
